import { useState } from 'react';
import { Button } from '../ui/button';
import { SuggestionRow, FoundBox } from './SuggestionRow';
import { roleLabel } from '../../lib/orgTypes';

// Groups whose title already says the role, so a row doesn't repeat it.
const ROLE_IN_TITLE = new Set(['social_worker', 'personal_adviser', 'iro', 'sibling']);
const SHOW_FIRST = 5;

const initials = (name) => (name || '?').split(/\s+/).filter((n) => n && !/^(of|and|mr|mrs|ms|miss|dr)$/i.test(n.replace('.', '')))
  .slice(0, 2).map((n) => n[0]).join('').toUpperCase() || '?';

const PersonRow = ({ person, group, onRemove, saving }) => {
  const bits = [
    !ROLE_IN_TITLE.has(group) || (group === 'sibling' && person.role !== 'Brother or sister') ? person.role : null,
    person.years,
    person.records ? `in ${person.records} record${person.records === 1 ? '' : 's'}` : null,
  ].filter(Boolean);
  return (
    <li className="flex items-center gap-3 py-2.5 border-b border-rule">
      <span aria-hidden="true" className="w-10 h-10 rounded-full bg-avatar text-ink flex items-center justify-center font-heading text-sm font-semibold shrink-0">{initials(person.name)}</span>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-ink truncate">{person.name}</p>
        {bits.length > 0 && <p className="text-sm text-ink-55">{bits.join(' · ')}</p>}
      </div>
      {onRemove && (
        <button type="button" onClick={onRemove} disabled={saving} className="text-sm text-ink-55 hover:text-breach-ink shrink-0" aria-label={`Remove ${person.name}`}>Remove</button>
      )}
    </li>
  );
};

/**
 * "The people who were there" — everyone the person added, the carers from
 * each home, short-break carers and (for a council, agency or adoption
 * body) brothers and sisters, in groups, each with what the records add.
 * People the records name that the person hasn't confirmed sit in the
 * found-in-your-records box underneath to add or put aside.
 */
export const OrgPeople = ({ org, groups, editing, onAddPerson, profileState }) => {
  const [showAll, setShowAll] = useState(false);
  const pending = (org.pending_suggestions || []).filter((s) => s.type === 'person' || s.type === 'sibling');
  const visible = showAll ? pending : pending.slice(0, SHOW_FIRST);
  const empty = groups.length === 0;

  return (
    <section aria-labelledby="org-people-heading" className="space-y-4" data-testid="people-card">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 id="org-people-heading" className="font-heading text-[24px] font-semibold tracking-[-0.02em] text-ink">The people who were there</h2>
        <button type="button" onClick={onAddPerson} className="text-sm font-semibold text-brand hover:text-brand-ink" data-testid="add-person-btn">Add someone</button>
      </div>

      {empty ? (
        <div className="rounded-lg bg-ground py-8 px-6 text-center">
          <p className="font-heading text-[22px] font-semibold tracking-[-0.02em] text-ink mb-1">No one added yet</p>
          <p className="text-base text-ink-55 mb-4 max-w-md mx-auto">Social workers, carers, teachers — anyone you remember from {org.name}. Names help them find your records.</p>
          <Button size="sm" variant="secondary" onClick={onAddPerson} data-testid="add-person-empty-btn">Add someone</Button>
        </div>
      ) : (
        <div className="space-y-6">
          {groups.map((g) => (
            <div key={g.id} data-testid={`people-group-${g.id}`}>
              <p className="pd-eyebrow mb-1">{groups.length === 1 && g.id === 'other' ? 'People named' : g.title}{g.id === 'social_worker' && g.people.length > 1 ? ', in order' : ''}</p>
              <ul className="grid sm:grid-cols-2 xl:grid-cols-3 gap-x-8">
                {g.people.map((p) => (
                  <PersonRow
                    key={`${p.name}-${p.index ?? p.siblingId ?? ''}`}
                    person={p} group={g.id} saving={editing.saving}
                    onRemove={p.index !== undefined ? () => editing.removePerson(p.index) : p.siblingId ? () => editing.removeSibling(p.siblingId) : null}
                  />
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {profileState === 'locked' && !empty && (
        <p className="text-sm text-ink-55">Unlock to see how often each person appears in the records.</p>
      )}

      {pending.length > 0 && (
        <FoundBox title={`Found in your records · ${pending.length} ${pending.length === 1 ? 'person' : 'people'}`} testId="people-suggestions">
          {visible.map((s) => (
            <SuggestionRow
              key={s.id} testId={`person-suggestion-${s.id}`} disabled={editing.saving}
              title={s.name}
              detail={s.type === 'sibling'
                ? (s.relationship && s.relationship !== 'Sibling' ? s.relationship : 'Brother or sister')
                : [s.role && roleLabel(org.type, s.role), s.dates_known].filter(Boolean).join(' · ')}
              actions={[{ label: 'Add', onClick: () => editing.confirmSuggestions([s.id]), testId: `confirm-suggestion-${s.id}` }]}
              onIgnore={() => editing.rejectSuggestions([s.id])}
            />
          ))}
          {pending.length > SHOW_FIRST && (
            <button type="button" onClick={() => setShowAll((v) => !v)} className="text-sm font-semibold text-brand hover:text-brand-ink">
              {showAll ? 'Show fewer' : `Show all ${pending.length}`}
            </button>
          )}
        </FoundBox>
      )}
    </section>
  );
};

export default OrgPeople;
